/// <amd-module name="Artifacts/Maven/components/MavenPackageAttributes" />
import { MavenCiConstants } from "Artifacts/Maven/Constants";
import * as MavenContracts from "Artifacts/Maven/Contracts/Maven";
import * as MavenRes from "Artifacts/Maven/Resources";
import { MavenDevelopment } from "Artifacts/Maven/components/MavenDevelopment";
import { MavenPackagePeople } from "Artifacts/Maven/components/MavenPackagePeople";
import { MavenDependencyHelper } from "Artifacts/Maven/helpers/MavenDependencyHelper";
import * as PackageRes from "Artifacts/Package/Resources";
import { PackageDependencies } from "Artifacts/Package/components/package-details-common/PackageDependencies";
import { IProjectAttribute } from "Artifacts/Package/components/package-details-common/PackageDevelopment";
import { PackageProvenance } from "Artifacts/Package/components/package-details-common/PackageProvenance";
import { PackageStats } from "Artifacts/Package/components/package-details-common/PackageStats";
import { PackageTags } from "Artifacts/Package/components/package-details-common/PackageTags";
import { IProtocolOverviewProps } from "Artifacts/Root/protocols/types";
import * as StringUtils from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import * as React from "react";

const MavenAttributes: React.FC<IProtocolOverviewProps> = ({ feed, pkg, version }) => {
  const { pageContext } = useComponentContext();

  const isProvenanceEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Provenance",
    false,
  );
  const isMetricsEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Metrics",
    false,
  );

  const protocolMetadata = version.protocolMetadata;
  const mavenMetadata = (protocolMetadata && protocolMetadata.data
    ? protocolMetadata.data
    : null) as MavenContracts.MavenPomMetadata;

  return (
    <section className="package-overview-attributes" aria-label={PackageRes.PackageMetadata}>
      <div className="maven-overview-attributes-panel"></div>
      <PackageProvenance
        pageContext={pageContext}
        feed={feed}
        packageId={pkg.id}
        versionId={version.id}
        publishDate={new Date(version.publishDate)}
        isProvenanceEnabled={isProvenanceEnabled}
        isProvenanceSupported={false}
      />

      {(mavenMetadata && mavenMetadata.developers && mavenMetadata.developers.length > 0) ||
      (mavenMetadata && mavenMetadata.contributors && mavenMetadata.contributors.length > 0) ? (
        <MavenPackagePeople
          pageContext={pageContext}
          developers={mavenMetadata.developers}
          contributors={mavenMetadata.contributors}
        />
      ) : null}

      {mavenMetadata != null && (
        <MavenDevelopment
          pageContext={pageContext}
          packageSummary={pkg}
          packageVersion={version}
          upstreamSources={feed.upstreamSources}
          buildAttribute={_getBuildAttribute(mavenMetadata.ciManagement)}
          codeAttribute={_getCodeAttribute(mavenMetadata.scm)}
          projectAttributes={_getProjectAttributes(
            mavenMetadata.organization,
            PreparePomLicenses(mavenMetadata.licenses),
            mavenMetadata.issueManagement,
          )}
        />
      )}

      {isMetricsEnabled && (
        <PackageStats
          pageContext={pageContext}
          feed={feed}
          package={pkg}
          versionId={version.id}
          isMetricsEnabled={isMetricsEnabled}
        />
      )}

      <PackageDependencies
        pageContext={pageContext}
        feed={feed}
        packageSummary={pkg}
        packageVersion={version}
        dependencyHelperCallback={MavenDependencyHelper.formatGroupNames}
      />
      <PackageTags title={PackageRes.PackageDetails_VersionsTable_Views} tags={version.views} />
    </section>
  );
};

function PreparePomLicenses(
  licenses: MavenContracts.MavenPomLicense[],
): MavenContracts.MavenPomLicense[] {
  if (licenses && licenses.length) {
    // Licenses can be named or unnamed.
    // 1. Order licenses by name
    // 2. Emit named licenses first
    // 3. Emit unnamed licenses next as "License {n}"
    // 4. IF there is only one ununnamed license - call it "License"
    // Test cases:
    // * Single unnamed license -> "License"
    // * Two named licenses (A, B) and two unnamed licenses -> "A", "B", "License 3", "License 4"

    // Check special case for a single unnamed license
    if (licenses.length === 1) {
      let license = licenses[0];
      if (!license.name) {
        license = _cloneLicense(license);
        license.name = MavenRes.MavenDevelopment_Licence;
      }
      return [license];
    } else {
      const namedLicenses = licenses.filter((license) => license.name).map(_cloneLicense);
      namedLicenses.sort((l1, l2) => StringUtils.localeComparer(l1.name, l2.name));

      let licenseCount = namedLicenses.length + 1;

      const unnamedLicenses = licenses
        .filter((license) => !license.name)
        .map((license) => {
          const clone = _cloneLicense(license);
          clone.name = StringUtils.format(MavenRes.MavenDevelopment_LicencePrefix, licenseCount++);
          return clone;
        });

      return namedLicenses.concat(unnamedLicenses);
    }
  }

  return licenses;
}

function _cloneLicense(license: MavenContracts.MavenPomLicense) {
  return {
    name: license.name,
    url: license.url,
    distribution: license.distribution,
  } as MavenContracts.MavenPomLicense;
}

function _getBuildAttribute(ciManagement: MavenContracts.MavenPomCi): IProjectAttribute | null {
  return ciManagement
    ? {
        iconClass: "Build",
        text: ciManagement.system || MavenRes.MavenDevelopment_ContinuousIntegration,
        url: ciManagement.url,
        onClickCiFeature: MavenCiConstants.BuildLinkClicked,
      }
    : null;
}

function _getCodeAttribute(scm: MavenContracts.MavenPomScm): IProjectAttribute | null {
  return scm
    ? {
        iconClass: "GitLogo",
        text: scm.tag || MavenRes.MavenDevelopment_SourceControl,
        url: scm.url,
        onClickCiFeature: MavenCiConstants.CodeLinkClicked,
      }
    : null;
}

function _getProjectAttributes(
  organization: MavenContracts.MavenPomOrganization,
  licenses: MavenContracts.MavenPomLicense[],
  issueManagement: MavenContracts.MavenPomIssueManagement,
): IProjectAttribute[] {
  const projectAttributes: IProjectAttribute[] = [];

  if (organization) {
    projectAttributes.push({
      iconClass: "Globe",
      text: organization.name || MavenRes.MavenDevelopment_OrganizationName,
      url: organization.url,
      onClickCiFeature: MavenCiConstants.OrganizationLinkClicked,
    } as IProjectAttribute);
  }

  if (licenses.length > 0) {
    licenses.forEach((license) => {
      projectAttributes.push({
        iconClass: "Script",
        text: license.name,
        url: license.url,
        onClickCiFeature: MavenCiConstants.LicenseLinkClicked,
      } as IProjectAttribute);
    });
  }

  if (issueManagement) {
    projectAttributes.push({
      iconClass: "FileBug",
      text: issueManagement.system || MavenRes.MavenDevelopment_IssueManagement,
      url: issueManagement.url,
      onClickCiFeature: MavenCiConstants.IssueLinkClicked,
    } as IProjectAttribute);
  }

  return projectAttributes;
}

export { MavenAttributes };
